import { Head } from 'vite-react-ssg'
import { absoluteUrl } from '@/shared/lib/env'
import { site } from '@/content/site'

type SeoProps = {
  /** Page title, without the site suffix. */
  title: string
  description: string
  /** Site-relative path, e.g. "/services/ui-ux-design". */
  path: string
  /** Site-relative or absolute OG image. Falls back to the site default. */
  image?: string
  type?: 'website' | 'article'
  noindex?: boolean
  /** JSON-LD objects to emit for this page. */
  jsonLd?: object[]
}

/**
 * The single way this site emits page metadata.
 *
 * Every page goes through here so no page can ship a partial set by accident —
 * a missing canonical or OG image is invisible until it costs you a ranking or
 * a broken LinkedIn preview.
 */
export function Seo({
  title,
  description,
  path,
  image = '/og/default.png',
  type = 'website',
  noindex = false,
  jsonLd = [],
}: SeoProps) {
  const fullTitle = path === '/' ? `${site.name} — ${site.tagline}` : `${title} | ${site.name}`
  const canonical = absoluteUrl(path)
  const imageUrl = image.startsWith('http') ? image : absoluteUrl(image)

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:site_name" content={site.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Head>
  )
}
